import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { Modal } from 'react-bootstrap-v5';
import Swal from 'sweetalert2';
import { IComprovante, IContaBancaria, IFiltroMinutas, IMinutaBancaria, IMinutasVisao, IPoliticaAutoaprovacao, MinutaMotivo, MinutaTipo } from '../../interfaces/IModuloBancario';
import ModuloBancarioService from '../../services/ModuloBancarioService';
import ModalAprovarLote from './modal-aprovar-lote';
import { PaginacaoModulo, useValorAtrasado } from './paginacao-modulo';

interface Props {
    esteiraId: number;
    /** "AAAA-MM"; vazio = nenhuma competência escolhida. */
    competencia: string;
    contas: IContaBancaria[];
    /** DEVN3-771: contas fechadas ou exportadas nesta competência; o servidor recusa as ações nelas. */
    contasBloqueadas: number[];
    onCarregou: () => void;
    /** Muda quando o fechamento mexe nas minutas (exportar, reabrir, conferir). */
    versaoFechamento: number;
}

const INTERVALO_GERACAO_MS = 3000;
const INTERVALO_SITUACAO_MS = 5000;
const LIMITE_LOTE = 500;
const VAZIO: IMinutasVisao = { geracaoEmAndamento: false, ultimaGeracao: null, minutas: [], total: 0, pagina: 1, tamanhoPagina: 50, contagens: {} };

/** "1.234,56" ou "1234.56" → número. */
const lerValor = (texto: string): number | undefined => {
    const limpo = texto.trim();
    if (!limpo) return undefined;
    const valor = Number(limpo.includes(',') ? limpo.replace(/\./g, '').replace(',', '.') : limpo);
    return isNaN(valor) ? undefined : valor;
};

const TIPO: Record<MinutaTipo, string> = {
    PROVISAO: 'Provisão',
    CATEGORIA: 'Categoria',
    TRANSFERENCIA: 'Transferência',
    COBRANCA: 'Cobrança',
    RFB: 'Pagamento RFB',
    NFSE: 'NFS-e',
    FOLHA: 'Folha',
    SEM_CANDIDATO: 'Sem candidato',
};

const ARRANJO_FOLHA: Record<string, string> = {
    TOTAL: 'total',
    LOTE: 'lote',
    INDIV: 'individual',
    PARC: 'parcial',
    MANU: 'vínculo manual',
};

const MOTIVO: Record<MinutaMotivo, string> = {
    CONTA: 'Conta',
    EVIDENCIA: 'Evidência',
    SALDO: 'Saldo',
    DIVERGENCIA: 'Divergência',
};

const brl = (valor: number | null | undefined) =>
    valor === null || valor === undefined ? '—' : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataBr = (valor: string | null | undefined) => (valor ? moment(valor).format('DD/MM/YYYY') : '—');
const descreverConta = (c?: IContaBancaria) =>
    c ? `${c.banco} / ${c.agencia ? `${c.agencia} / ` : ''}${c.conta}${c.descricao ? ` — ${c.descricao}` : ''}` : '—';

const ORIGEM_AUTOAPROVACAO: Record<string, string> = { PONTUACAO: 'pontuação acima de 99,00', TARIFA: 'tarifa igual à última aprovada' };

// DEVN3-773: corta em vez de arredondar, para 99,004 não aparecer como 99,01 nem 99,999 como 100.
const nota = (valor: number) => (Math.floor(Math.round(valor * 1e6) / 1e4) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Situacao({ minuta }: { minuta: IMinutaBancaria }) {
    if (minuta.situacao === 'GERADA')
        return <span className='badge badge-light-success'>Gerada{minuta.escolhaManual && ' (escolhida)'}</span>;
    if (minuta.situacao === 'PENDENTE')
        return <span className='badge badge-light-warning'>Pendente{minuta.motivo && ` · ${MOTIVO[minuta.motivo]}`}</span>;
    if (minuta.situacao === 'APROVADA')
        return minuta.autoaprovacaoOrigem
            ? <span className='badge badge-success' title={`Aprovada pelo sistema: ${ORIGEM_AUTOAPROVACAO[minuta.autoaprovacaoOrigem]} · política v${minuta.politicaVersao}`}>Autoaprovada</span>
            : <span className='badge badge-success'>Aprovada</span>;
    if (minuta.situacao === 'REPROVADA')
        return <span className='badge badge-light-danger'>Reprovada</span>;
    if (minuta.situacao === 'EXPORTADA')
        return <span className='badge badge-primary'>Exportada</span>;
    if (minuta.situacao === 'CONTABILIZADA')
        return <span className='badge badge-light-primary'>Contabilizada</span>;
    return <span className='badge badge-light-primary'>{minuta.situacao}</span>;
}

// DEVN3-769 — minutas (E3): partidas propostas para as linhas do extrato que a conciliação razão ×
// extrato deixou pendentes. Ficam fora do razão até serem aprovadas, exportadas e reimportadas.
export default function MinutasBancarias({ esteiraId, competencia, contas, contasBloqueadas, onCarregou, versaoFechamento }: Props) {
    const [visao, setVisao] = useState<IMinutasVisao>(VAZIO);
    const [carregando, setCarregando] = useState(false);
    const [filtroConta, setFiltroConta] = useState<number>(0);
    const [filtroSituacao, setFiltroSituacao] = useState<string>('');
    const [filtroTipo, setFiltroTipo] = useState<string>('');
    const [historico, setHistorico] = useState('');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');
    const [valorMinimo, setValorMinimo] = useState('');
    const [valorMaximo, setValorMaximo] = useState('');
    const [pagina, setPagina] = useState(1);
    const [tamanhoPagina, setTamanhoPagina] = useState(50);
    const [abertas, setAbertas] = useState<Set<number>>(new Set());
    const [escolha, setEscolha] = useState<{ minuta: IMinutaBancaria; opcao: number } | null>(null);
    const [salvando, setSalvando] = useState(false);
    // A seleção atravessa páginas e filtros: guarda a minuta para a prévia do lote descrever a linha.
    const [marcadas, setMarcadas] = useState<Map<number, IMinutaBancaria>>(new Map());
    const [lote, setLote] = useState<number[] | null>(null);
    const [anexo, setAnexo] = useState<{ minuta: IMinutaBancaria; opcoes: IComprovante[]; escolhido: number } | null>(null);
    const [politica, setPolitica] = useState<IPoliticaAutoaprovacao | null>(null);
    const timer = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        ModuloBancarioService.politicaAutoaprovacao(esteiraId).then(setPolitica).catch(() => setPolitica(null));
    }, [esteiraId]);

    const [ano, mes] = competencia ? competencia.split('-').map(Number) : [0, 0];
    const contasPorId = useMemo(() => new Map(contas.map(c => [c.id, c])), [contas]);

    const historicoAtrasado = useValorAtrasado(historico);
    const valorMinimoAtrasado = useValorAtrasado(valorMinimo);
    const valorMaximoAtrasado = useValorAtrasado(valorMaximo);

    const filtrosParams = useMemo<Omit<IFiltroMinutas, 'pagina' | 'tamanhoPagina'>>(() => ({
        contaBancariaId: filtroConta || undefined,
        situacao: filtroSituacao || undefined,
        tipo: filtroTipo || undefined,
        historico: historicoAtrasado.trim() || undefined,
        dataInicio: dataInicio || undefined,
        dataFim: dataFim || undefined,
        valorMinimo: lerValor(valorMinimoAtrasado),
        valorMaximo: lerValor(valorMaximoAtrasado),
    }), [filtroConta, filtroSituacao, filtroTipo, historicoAtrasado, dataInicio, dataFim, valorMinimoAtrasado, valorMaximoAtrasado]);

    // Mudar um filtro volta à primeira página.
    useEffect(() => { setPagina(1); }, [filtrosParams]);

    const filtrando = !!(filtroConta || filtroSituacao || filtroTipo || historico || dataInicio || dataFim || valorMinimo || valorMaximo);
    const limparFiltros = () => {
        setFiltroConta(0);
        setFiltroSituacao('');
        setFiltroTipo('');
        setHistorico('');
        setDataInicio('');
        setDataFim('');
        setValorMinimo('');
        setValorMaximo('');
    };

    const buscar = useCallback(async (silencioso = false) => {
        if (!ano || !mes) return;
        if (!silencioso) setCarregando(true);
        try {
            setVisao(await ModuloBancarioService.minutas(esteiraId, ano, mes, { ...filtrosParams, pagina, tamanhoPagina }));
            onCarregou();
        } catch (err: any) {
            if (!silencioso) Swal.fire({ text: err?.message ?? 'Não foi possível carregar as minutas.', icon: 'error' });
        } finally {
            if (!silencioso) setCarregando(false);
        }
    }, [esteiraId, ano, mes, onCarregou, filtrosParams, pagina, tamanhoPagina]);

    useEffect(() => {
        if (versaoFechamento > 0) buscar(true);
    }, [versaoFechamento, buscar]);

    useEffect(() => {
        setVisao(VAZIO);
        setMarcadas(new Map());
    }, [esteiraId, ano, mes]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    // Uma geração refaz as minutas regeneráveis: os ids marcados deixam de valer.
    const operacaoDaSelecao = useRef<number | null | undefined>();
    useEffect(() => {
        if (operacaoDaSelecao.current !== undefined && operacaoDaSelecao.current !== (visao.ultimaOperacaoId ?? null)) setMarcadas(new Map());
        if (visao !== VAZIO) operacaoDaSelecao.current = visao.ultimaOperacaoId ?? null;
    }, [visao]);

    // Enquanto a geração roda no Worker, recarrega até ela terminar.
    useEffect(() => {
        if (!visao.geracaoEmAndamento) return;
        timer.current = setTimeout(() => buscar(true), INTERVALO_GERACAO_MS);
        return () => clearTimeout(timer.current);
    }, [visao, buscar]);

    // A geração também nasce de documentos, extratos e do Setup, fora desta seção.
    const operacaoConhecida = useRef<number | null | undefined>();
    operacaoConhecida.current = visao.ultimaOperacaoId;
    useEffect(() => {
        if (!ano || !mes) return;
        const id = setInterval(async () => {
            if (document.hidden) return;
            try {
                const situacao = await ModuloBancarioService.situacaoMinutas(esteiraId, ano, mes);
                if (situacao.geracaoEmAndamento) setVisao(v => (v.geracaoEmAndamento ? v : { ...v, geracaoEmAndamento: true }));
                else if ((situacao.ultimaOperacaoId ?? null) !== (operacaoConhecida.current ?? null)) buscar(true);
            } catch {
                // Consulta de fundo: a próxima tenta de novo.
            }
        }, INTERVALO_SITUACAO_MS);
        return () => clearInterval(id);
    }, [esteiraId, ano, mes, buscar]);

    const gerar = async () => {
        try {
            await ModuloBancarioService.gerarMinutas(esteiraId, ano, mes);
            setVisao(v => ({ ...v, geracaoEmAndamento: true }));
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível iniciar a geração.', icon: 'error' });
        }
    };

    const confirmarEscolha = async () => {
        if (!escolha) return;
        setSalvando(true);
        try {
            await ModuloBancarioService.escolherCandidato(esteiraId, escolha.minuta.id, escolha.opcao);
            setEscolha(null);
            buscar(true);
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível gerar a minuta com esta opção.', icon: 'error' });
        } finally {
            setSalvando(false);
        }
    };

    const alternar = (id: number) =>
        setAbertas(atual => {
            const novo = new Set(atual);
            if (novo.has(id)) novo.delete(id); else novo.add(id);
            return novo;
        });

    const decidir = async (acao: () => Promise<void>, erro: string) => {
        try {
            await acao();
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? erro, icon: 'error' });
        }
        setMarcadas(new Map());
        buscar(true);
    };

    // DEVN3-772: só os comprovantes válidos e livres, do mesmo valor da minuta.
    const abrirAnexo = async (m: IMinutaBancaria) => {
        const valor = m.partidas.filter(p => p.natureza === 'D').reduce((s, p) => s + p.valor, 0);
        try {
            const { comprovantes } = await ModuloBancarioService.comprovantes(esteiraId, ano, mes, { valorLivre: valor, tamanhoPagina: 100 });
            setAnexo({ minuta: m, opcoes: comprovantes, escolhido: 0 });
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível carregar os comprovantes.', icon: 'error' });
        }
    };

    const confirmarAnexo = async () => {
        if (!anexo?.escolhido) return;
        const { minuta, escolhido } = anexo;
        setAnexo(null);
        await decidir(() => ModuloBancarioService.anexarComprovante(esteiraId, minuta.id, escolhido), 'Não foi possível anexar o comprovante.');
    };

    const desanexar = (m: IMinutaBancaria) =>
        decidir(() => ModuloBancarioService.desanexarComprovante(esteiraId, m.id), 'Não foi possível retirar o comprovante.');

    const aprovar = (m: IMinutaBancaria) =>
        decidir(() => ModuloBancarioService.aprovarMinuta(esteiraId, m.id), 'Não foi possível aprovar a minuta.');

    const reprovar = async (m: IMinutaBancaria) => {
        const { value } = await Swal.fire({
            title: 'Reprovar minuta',
            text: 'A provisão fica livre e a linha do extrato volta a ficar pendente, com este motivo.',
            input: 'textarea',
            inputPlaceholder: 'Motivo da reprovação',
            inputAttributes: { maxlength: '500' },
            showCancelButton: true,
            confirmButtonText: 'Reprovar',
            cancelButtonText: 'Cancelar',
            inputValidator: v => (v.trim() ? null : 'Informe o motivo.'),
        });
        if (value) decidir(() => ModuloBancarioService.reprovarMinuta(esteiraId, m.id, value), 'Não foi possível reprovar a minuta.');
    };

    const desfazerReprovacao = async (m: IMinutaBancaria) => {
        const { isConfirmed } = await Swal.fire({
            text: 'Desfazer a reprovação? A minuta sai da lista e a linha volta a ser considerada na próxima geração.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Desfazer',
            cancelButtonText: 'Cancelar',
        });
        if (isConfirmed) decidir(() => ModuloBancarioService.desfazerReprovacao(esteiraId, m.id), 'Não foi possível desfazer a reprovação.');
    };

    const desaprovar = (m: IMinutaBancaria) =>
        decidir(() => ModuloBancarioService.desaprovarMinuta(esteiraId, m.id), 'Não foi possível desaprovar a minuta.');

    // DEVN3-778: só registra, para a assertividade. Para desfazer a minuta, continua o Desaprovar.
    const revisar = async (m: IMinutaBancaria) => {
        const { value, isConfirmed, isDenied } = await Swal.fire({
            title: 'Revisar minuta autoaprovada',
            text: 'A revisão entra na assertividade e não muda a minuta. O motivo é obrigatório quando ela está incorreta.',
            input: 'textarea',
            inputPlaceholder: 'Motivo',
            showDenyButton: true,
            showCancelButton: true,
            confirmButtonText: 'Correta',
            denyButtonText: 'Incorreta',
            cancelButtonText: 'Cancelar',
            // O botão de negar não recebe o valor do campo.
            preDeny: () => {
                const motivo = (Swal.getInput() as HTMLTextAreaElement | null)?.value.trim();
                if (motivo) return motivo;
                Swal.showValidationMessage('Informe o motivo.');
                return false;
            },
        });
        if (isConfirmed) decidir(() => ModuloBancarioService.revisarMinuta(esteiraId, m.id, 'CORRETA', value?.trim() || undefined), 'Não foi possível registrar a revisão.');
        else if (isDenied) decidir(() => ModuloBancarioService.revisarMinuta(esteiraId, m.id, 'INCORRETA', value), 'Não foi possível registrar a revisão.');
    };

    const alternarPolitica = async () => {
        if (!politica) return;
        const ligada = !politica.ligada;
        const { isConfirmed } = await Swal.fire({
            title: ligada ? 'Ligar a autoaprovação' : 'Desligar a autoaprovação',
            text: ligada
                ? 'O sistema vai aprovar sozinho as minutas com pontuação acima de 99,00 e as tarifas iguais à última aprovada por uma pessoa, desde que não tenham impedimento. As competências abertas serão geradas de novo.'
                : 'As próximas minutas ficam para aprovação de uma pessoa. O que já foi aprovado não muda.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: ligada ? 'Ligar' : 'Desligar',
            cancelButtonText: 'Cancelar',
        });
        if (!isConfirmed) return;
        try {
            setPolitica(await ModuloBancarioService.alterarPoliticaAutoaprovacao(esteiraId, ligada));
            buscar(true);
        } catch (err: any) {
            Swal.fire({ text: err?.message ?? 'Não foi possível alterar a política de autoaprovação.', icon: 'error' });
        }
    };

    const minutas = visao.minutas;
    const contagem = (situacao: string) => visao.contagens[situacao] ?? 0;
    const totalCompetencia = Object.values(visao.contagens).reduce((s, n) => s + n, 0);
    const n = (valor: number) => valor.toLocaleString('pt-BR');

    // Só minuta Gerada entra no lote. A seleção vale entre páginas e aparece na barra acima da lista.
    const marcaveis = minutas.filter(m => m.situacao === 'GERADA');
    const selecionadas = Array.from(marcadas.keys());
    const paginaMarcada = marcaveis.length > 0 && marcaveis.every(m => marcadas.has(m.id));
    const alternarMarcada = (m: IMinutaBancaria) =>
        setMarcadas(atual => {
            const novo = new Map(atual);
            if (novo.has(m.id)) novo.delete(m.id); else novo.set(m.id, m);
            return novo;
        });
    const alternarPagina = () =>
        setMarcadas(atual => {
            const novo = new Map(atual);
            marcaveis.forEach(m => { if (paginaMarcada) novo.delete(m.id); else novo.set(m.id, m); });
            return novo;
        });

    // Atalhos do resumo: levam direto ao que precisa de decisão.
    const ATALHOS: { situacao: string; nome: string; quantidade: number; classe: string }[] = [
        { situacao: 'PENDENTE', nome: 'Pendentes', quantidade: contagem('PENDENTE'), classe: 'badge-light-warning' },
        { situacao: 'GERADA', nome: 'Geradas', quantidade: contagem('GERADA'), classe: 'badge-light-success' },
        { situacao: 'APROVADA', nome: 'Aprovadas', quantidade: contagem('APROVADA'), classe: 'badge-success' },
        { situacao: 'REPROVADA', nome: 'Reprovadas', quantidade: contagem('REPROVADA'), classe: 'badge-light-danger' },
        { situacao: 'EXPORTADA', nome: 'Exportadas', quantidade: contagem('EXPORTADA'), classe: 'badge-primary' },
        { situacao: 'CONTABILIZADA', nome: 'Contabilizadas', quantidade: contagem('CONTABILIZADA'), classe: 'badge-light-primary' },
    ];

    return (
        <div className='card card-flush mb-4'>
            <div className='card-header align-items-center'>
                <h3 className='card-title fw-bold'>
                    Minutas
                    {(carregando || visao.geracaoEmAndamento) && <span className='spinner-border spinner-border-sm ms-3'></span>}
                </h3>
                <div className='d-flex flex-wrap align-items-center gap-2'>
                    {politica && (
                        <span className='d-flex align-items-center gap-2 fs-7 me-2'>
                            <span className={clsx('badge', politica.ligada ? 'badge-light-success' : 'badge-light')}
                                title={politica.versao > 0 ? `Política v${politica.versao}` : undefined}>
                                Autoaprovação {politica.ligada ? 'ligada' : 'desligada'}
                            </span>
                            {politica.podeAlterar && (
                                <button className='btn btn-sm btn-light' onClick={alternarPolitica}>{politica.ligada ? 'Desligar' : 'Ligar'}</button>
                            )}
                        </span>
                    )}
                    {selecionadas.length > 0 && (
                        <button className='btn btn-sm btn-success' disabled={selecionadas.length > LIMITE_LOTE} onClick={() => setLote(selecionadas)}
                            title={selecionadas.length > LIMITE_LOTE ? `O lote aceita até ${LIMITE_LOTE} minutas` : undefined}>
                            <i className='fas fa-check-double me-1'></i>Aprovar {selecionadas.length} selecionada(s)
                        </button>
                    )}
                    <button className='btn btn-sm btn-primary' disabled={!competencia || visao.geracaoEmAndamento || contas.length === 0} onClick={gerar}
                        title={!competencia ? 'Escolha a competência' : undefined}>
                        <i className='fas fa-cogs me-1'></i>{totalCompetencia > 0 ? 'Gerar de novo' : 'Gerar minutas'}
                    </button>
                </div>
            </div>
            <div className='card-body pt-0'>
                {!competencia ? (
                    <div className='text-muted fs-7'>Escolha a competência para gerar e ver as minutas.</div>
                ) : (
                    <>
                        <div className='text-muted fs-7 mb-3'>
                            Linhas do extrato sem lançamento no razão. Gerar de novo refaz as pendentes e as geradas automaticamente; as escolhidas, aprovadas e reprovadas por você ficam.
                            {/* O backend grava em UTC sem o "Z". */}
                            {visao.ultimaGeracao && <> Última geração em {moment.utc(visao.ultimaGeracao).local().format('DD/MM/YYYY HH:mm')}.</>}
                        </div>
                        {totalCompetencia > 0 && (
                            <>
                                <div className='d-flex flex-wrap gap-2 mb-3'>
                                    {ATALHOS.filter(a => a.quantidade > 0).map(a => (
                                        <button key={a.situacao} type='button' title={`Filtrar: ${a.nome}`} aria-pressed={filtroSituacao === a.situacao}
                                            onClick={() => setFiltroSituacao(filtroSituacao === a.situacao ? '' : a.situacao)}
                                            className={clsx('badge fs-7 cursor-pointer border', a.classe, filtroSituacao === a.situacao ? 'border-2 border-primary' : 'border-transparent')}>
                                            {a.nome}: {n(a.quantidade)}
                                        </button>
                                    ))}
                                </div>
                                <div className='d-flex flex-wrap align-items-end gap-2 mb-3'>
                                    <div style={{ flex: '1 1 220px' }}>
                                        <label className='form-label fs-8 mb-1'>Histórico</label>
                                        <input className='form-control form-control-sm' placeholder='Trecho do extrato ou da partida' value={historico} onChange={e => setHistorico(e.target.value)} />
                                    </div>
                                    <div>
                                        <label className='form-label fs-8 mb-1'>Conta bancária</label>
                                        <select className='form-select form-select-sm' style={{ width: 220 }} value={filtroConta} onChange={e => setFiltroConta(Number(e.target.value))}>
                                            <option value={0}>Todas</option>
                                            {contas.map(c => <option key={c.id} value={c.id}>{descreverConta(c)}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className='form-label fs-8 mb-1'>Situação</label>
                                        <select className='form-select form-select-sm' style={{ width: 180 }} value={filtroSituacao} onChange={e => setFiltroSituacao(e.target.value)}>
                                            <option value=''>Todas</option>
                                            <option value='PENDENTE'>Pendentes</option>
                                            {(Object.keys(MOTIVO) as MinutaMotivo[]).map(m => <option key={m} value={m}>Pendentes · {MOTIVO[m]}</option>)}
                                            <option value='GERADA'>Geradas</option>
                                            <option value='APROVADA'>Aprovadas</option>
                                            <option value='AUTOAPROVADA'>Autoaprovadas</option>
                                            <option value='REPROVADA'>Reprovadas</option>
                                            <option value='EXPORTADA'>Exportadas</option>
                                            <option value='CONTABILIZADA'>Contabilizadas</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className='form-label fs-8 mb-1'>Tipo</label>
                                        <select className='form-select form-select-sm' style={{ width: 150 }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
                                            <option value=''>Todos</option>
                                            {(Object.keys(TIPO) as MinutaTipo[]).map(t => <option key={t} value={t}>{TIPO[t]}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className='form-label fs-8 mb-1'>Data da linha</label>
                                        <div className='d-flex gap-1'>
                                            <input type='date' className='form-control form-control-sm' aria-label='Data inicial' value={dataInicio} onChange={e => setDataInicio(e.target.value)} />
                                            <input type='date' className='form-control form-control-sm' aria-label='Data final' value={dataFim} onChange={e => setDataFim(e.target.value)} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className='form-label fs-8 mb-1'>Valor da linha</label>
                                        <div className='d-flex gap-1'>
                                            <input className='form-control form-control-sm' style={{ width: 100 }} inputMode='decimal' placeholder='De' aria-label='Valor mínimo' value={valorMinimo} onChange={e => setValorMinimo(e.target.value)} />
                                            <input className='form-control form-control-sm' style={{ width: 100 }} inputMode='decimal' placeholder='Até' aria-label='Valor máximo' value={valorMaximo} onChange={e => setValorMaximo(e.target.value)} />
                                        </div>
                                    </div>
                                    {filtrando && <button className='btn btn-sm btn-link p-0 mb-2' onClick={limparFiltros}>Limpar filtros</button>}
                                </div>
                            </>
                        )}
                        {selecionadas.length > 0 && (
                            <div className='d-flex flex-wrap align-items-center gap-3 bg-light-primary rounded p-3 mb-3 fs-7'>
                                <span className='flex-grow-1'>
                                    <strong>{n(selecionadas.length)}</strong> minuta(s) selecionada(s), inclusive em outras páginas.
                                    {selecionadas.length > LIMITE_LOTE && <span className='text-danger ms-1'>O lote aceita até {n(LIMITE_LOTE)}.</span>}
                                </span>
                                <button className='btn btn-sm btn-link p-0' onClick={() => setMarcadas(new Map())}>Limpar seleção</button>
                            </div>
                        )}
                        {visao.minutas.length === 0 ? (
                            !carregando && !visao.geracaoEmAndamento && <div className='text-muted py-4'>{filtrando ? 'Nenhuma minuta com estes filtros.' : 'Nenhuma minuta nesta competência.'}</div>
                        ) : (
                            <div className='table-responsive'>
                            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-2'>
                                <thead>
                                    <tr className='fw-bold text-muted'>
                                        <th style={{ width: 30 }}>
                                            <input type='checkbox' className='form-check-input' title='Marcar as geradas desta página' aria-label='Marcar as geradas desta página' disabled={marcaveis.length === 0} checked={paginaMarcada}
                                                onChange={alternarPagina} />
                                        </th>
                                        <th style={{ width: 30 }}></th>
                                        <th>Data</th>
                                        <th>Conta bancária</th>
                                        <th>Histórico do extrato</th>
                                        <th className='text-end'>Valor</th>
                                        <th>Tipo</th>
                                        <th className='text-end'>Pontuação</th>
                                        <th>Situação</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {minutas.map(m => {
                                        const linha = m.linhas[0];
                                        const aberta = abertas.has(m.id);
                                        return (
                                            <Fragment key={m.id}>
                                                <tr>
                                                    <td>
                                                        {m.situacao === 'GERADA' && (
                                                            <input type='checkbox' className='form-check-input' aria-label='Selecionar minuta' checked={marcadas.has(m.id)} onChange={() => alternarMarcada(m)} />
                                                        )}
                                                    </td>
                                                    <td>
                                                        {m.partidas.length > 0 && (
                                                            <button className='btn btn-sm btn-icon btn-light' title='Partidas' onClick={() => alternar(m.id)}>
                                                                <i className={clsx('fas', aberta ? 'fa-chevron-down' : 'fa-chevron-right')}></i>
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td>{dataBr(linha?.data)}</td>
                                                    <td className='fs-7'>
                                                        {descreverConta(contasPorId.get(m.contaBancariaId))}
                                                        {m.contaBancariaDestinoId && <div className='text-muted'>→ {descreverConta(contasPorId.get(m.contaBancariaDestinoId))}</div>}
                                                    </td>
                                                    <td className='fs-7' style={{ maxWidth: 320 }}>
                                                        {m.linhas.map(l => <div key={l.id}>{l.historico ?? '—'}</div>)}
                                                    </td>
                                                    <td className='text-end text-nowrap'>
                                                        {m.linhas.map(l => (
                                                            <div key={l.id} className={l.tipo === 'D' ? 'text-danger' : 'text-success'}>
                                                                {l.tipo === 'D' ? '−' : '+'}{brl(l.valor)}
                                                            </div>
                                                        ))}
                                                    </td>
                                                    <td className='fs-7'>
                                                        {TIPO[m.tipo]}{m.arranjo && ` ${ARRANJO_FOLHA[m.arranjo] ?? m.arranjo}`}
                                                        {m.categoriaNome && <div className='text-muted'>{m.categoriaNome}{m.setupVersao && ` · v${m.setupVersao}`}</div>}
                                                    </td>
                                                    <td className='text-end text-nowrap fs-7'
                                                        title={m.pontuacaoDetalhe ? `Valor ${nota(m.pontuacaoDetalhe.valor)} · Data ${nota(m.pontuacaoDetalhe.data)} · Histórico ${nota(m.pontuacaoDetalhe.historico)} · Evidência ${nota(m.pontuacaoDetalhe.evidencia)}` : undefined}>
                                                        {m.pontuacao === null ? '—' : nota(m.pontuacao)}
                                                    </td>
                                                    <td style={{ maxWidth: 320 }}>
                                                        <Situacao minuta={m} />
                                                        {m.mensagem && <div className='text-muted fs-8 mt-1'>{m.mensagem}</div>}
                                                        {m.motivoReprovacao && <div className='text-danger fs-8 mt-1'>{m.motivoReprovacao}</div>}
                                                        {m.comprovante && (
                                                            <div className='text-muted fs-8 mt-1'>
                                                                <i className='fas fa-paperclip me-1'></i>
                                                                Comprovante {m.comprovante.tipo ?? ''} {m.comprovante.nome ?? ''}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className='text-end'>
                                                        {m.autoaprovacaoOrigem && (
                                                            <div className='mb-1'>
                                                                {m.revisao && (
                                                                    <span className={clsx('badge me-1', m.revisao.resultado === 'CORRETA' ? 'badge-light-success' : 'badge-light-danger')} title={m.revisao.motivo ?? undefined}>
                                                                        {m.revisao.resultado === 'CORRETA' ? 'Revisada: correta' : 'Revisada: incorreta'}
                                                                    </span>
                                                                )}
                                                                <button className='btn btn-sm btn-light-primary' onClick={() => revisar(m)}>{m.revisao ? 'Rever' : 'Revisar'}</button>
                                                            </div>
                                                        )}
                                                        {(contasBloqueadas.includes(m.contaBancariaId) || (m.contaBancariaDestinoId !== null && contasBloqueadas.includes(m.contaBancariaDestinoId))) ? (
                                                            <span className='text-muted fs-8'>Conta fechada ou exportada</span>
                                                        ) : <div className='d-flex flex-wrap gap-1 justify-content-end'>
                                                            {m.situacao === 'PENDENTE' && m.motivo === 'EVIDENCIA' && m.candidatos.length > 0 && (
                                                                <button className='btn btn-sm btn-light-primary' onClick={() => setEscolha({ minuta: m, opcao: 0 })}>Escolher</button>
                                                            )}
                                                            {m.situacao === 'GERADA' && (
                                                                <>
                                                                    <button className='btn btn-sm btn-light-success' onClick={() => aprovar(m)}>Aprovar</button>
                                                                    <button className='btn btn-sm btn-light-danger' onClick={() => reprovar(m)}>Reprovar</button>
                                                                </>
                                                            )}
                                                            {m.situacao === 'APROVADA' && (
                                                                <button className='btn btn-sm btn-light' onClick={() => desaprovar(m)}>Desaprovar</button>
                                                            )}
                                                            {m.situacao === 'REPROVADA' && (
                                                                <button className='btn btn-sm btn-light' onClick={() => desfazerReprovacao(m)}>Desfazer</button>
                                                            )}
                                                            {(m.situacao === 'GERADA' || m.situacao === 'APROVADA') && (
                                                                m.comprovante
                                                                    ? <button className='btn btn-sm btn-icon btn-light' title='Retirar comprovante' onClick={() => desanexar(m)}><i className='fas fa-unlink'></i></button>
                                                                    : <button className='btn btn-sm btn-icon btn-light' title='Anexar comprovante' onClick={() => abrirAnexo(m)}><i className='fas fa-paperclip'></i></button>
                                                            )}
                                                        </div>}
                                                    </td>
                                                </tr>
                                                {aberta && (
                                                    <tr>
                                                        <td colSpan={2}></td>
                                                        <td colSpan={8}>
                                                            <table className='table table-sm fs-7 mb-0'>
                                                                <thead>
                                                                    <tr className='text-muted'>
                                                                        <th>Data</th>
                                                                        <th>Conta</th>
                                                                        <th className='text-end'>Débito</th>
                                                                        <th className='text-end'>Crédito</th>
                                                                        <th>Histórico</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {m.partidas.map(p => (
                                                                        <tr key={p.sequencia}>
                                                                            <td>{dataBr(p.data)}</td>
                                                                            <td>{p.contaContabil} <span className='text-muted'>{p.contaDescricao}</span></td>
                                                                            <td className='text-end'>{p.natureza === 'D' ? brl(p.valor) : ''}</td>
                                                                            <td className='text-end'>{p.natureza === 'C' ? brl(p.valor) : ''}</td>
                                                                            <td>
                                                                                {p.historico}
                                                                                {p.origemHistorico === 'PROVISAO' && <span className='badge badge-light ms-2'>da provisão</span>}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </td>
                                                    </tr>
                                                )}
                                            </Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </div>
                        )}
                        <PaginacaoModulo total={visao.total} pagina={pagina} tamanhoPagina={tamanhoPagina} onPagina={setPagina} onTamanho={t => { setTamanhoPagina(t); setPagina(1); }} />
                    </>
                )}
            </div>

            {lote && (
                <ModalAprovarLote
                    esteiraId={esteiraId}
                    ano={ano}
                    mes={mes}
                    minutaIds={lote}
                    minutas={Array.from(marcadas.values())}
                    contas={contas}
                    onHide={() => setLote(null)}
                    onAprovado={() => { setMarcadas(new Map()); buscar(true); }}
                />
            )}

            {anexo && (
                <Modal show onHide={() => setAnexo(null)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Anexar comprovante</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {anexo.opcoes.length === 0 ? (
                            <div className='text-muted'>Nenhum comprovante válido e livre com o valor desta minuta nesta competência.</div>
                        ) : anexo.opcoes.map(c => (
                            <label key={c.id} className={clsx('d-flex align-items-start gap-3 p-3 mb-2 border rounded cursor-pointer', anexo.escolhido === c.id && 'border-primary bg-light-primary')}>
                                <input type='radio' className='form-check-input mt-1' checked={anexo.escolhido === c.id} onChange={() => setAnexo({ ...anexo, escolhido: c.id })} />
                                <div className='fs-7'>
                                    <div className='fw-bold'>{c.nome ?? 'Sem nome'}</div>
                                    <div className='text-muted'>{c.tipo ?? ''} · {dataBr(c.dataLiquidacao)} · {brl(c.valor)}</div>
                                </div>
                            </label>
                        ))}
                    </Modal.Body>
                    <Modal.Footer>
                        <button className='btn btn-light' onClick={() => setAnexo(null)}>Cancelar</button>
                        <button className='btn btn-primary' disabled={!anexo.escolhido} onClick={confirmarAnexo}>Anexar</button>
                    </Modal.Footer>
                </Modal>
            )}

            {escolha && (
                <Modal show onHide={() => setEscolha(null)} centered size='lg'>
                    <Modal.Header closeButton>
                        <Modal.Title>Escolher candidato</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='mb-4'>
                            {escolha.minuta.linhas.map(l => (
                                <div key={l.id}>
                                    <strong>{dataBr(l.data)}</strong> · {l.historico} · <strong>{brl(l.valor)}</strong> ({l.tipo === 'D' ? 'saída' : 'entrada'})
                                </div>
                            ))}
                            {escolha.minuta.mensagem && <div className='text-muted fs-7 mt-1'>{escolha.minuta.mensagem}</div>}
                        </div>
                        {escolha.minuta.candidatos.map((c, i) => (
                            <label key={i} className={clsx('d-flex align-items-start gap-3 p-3 mb-2 border rounded cursor-pointer', escolha.opcao === i && 'border-primary bg-light-primary')}>
                                <input type='radio' className='form-check-input mt-1' checked={escolha.opcao === i} onChange={() => setEscolha({ ...escolha, opcao: i })} />
                                <div>
                                    <div className='fw-bold'>{c.descricao}</div>
                                    <div className='fs-7 text-muted'>{dataBr(c.data)} · {brl(c.valor)}{c.historico && ` · ${c.historico}`}</div>
                                </div>
                            </label>
                        ))}
                    </Modal.Body>
                    <Modal.Footer>
                        <button className='btn btn-light' onClick={() => setEscolha(null)}>Cancelar</button>
                        <button className='btn btn-primary' disabled={salvando} onClick={confirmarEscolha}>
                            {salvando && <span className='spinner-border spinner-border-sm me-2'></span>}Gerar a minuta
                        </button>
                    </Modal.Footer>
                </Modal>
            )}
        </div>
    );
}
